import { Platform } from 'react-native';
import { CommonActions } from '@react-navigation/native';
import { t } from 'i18next';
import type { QueryClient } from '@tanstack/react-query';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { store, persistor } from '@reduxLocal/persistState';
import {
  HIDE_ALL_MODALS,
  HIDE_LOADING_MODAL,
  LOGGED_OUT,
  SHOW_AUTH_MODAL,
  SHOW_LOADING_MODAL,
} from '@reduxLocal/actions/types';
import { navigationRef } from '@navigation/rootNavigation';
import SocketManager from '@utils/socket';
import { abortAllRequests } from '@utils/abortController';
import { clearAuthGracePeriod, isWithinAuthGracePeriod } from '@utils/authGracePeriod';
import { clearOfflineModeEntitlement } from '@api/utils/offlineEntitlement';
import { clearSessionCookie } from '@utils/sessionCookie';
import { clearPersistentSessionMeta } from '@utils/persistentSessionStorage';
import { isDashboardPath } from '@utils/hasPersistedWebSession';
import {
  clearPendingDashboardDeepLink,
  stashPendingDashboardDeepLink,
} from '@navigation/utils/pendingDashboardDeepLink';
import { getAuthSessionGuard } from '@utils/authSessionGuard';
import { clearWatchSessionLater } from '@services/watchSessionSync';

const withTimeout = async <T,>(
  promise: Promise<T>,
  ms: number,
  label: string
): Promise<T> => {
  let timeout: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        timeout = setTimeout(
          () => reject(new Error(`[logout] ${label} timed out after ${ms}ms`)),
          ms
        );
      }),
    ]);
  } finally {
    if (timeout) clearTimeout(timeout);
  }
};

/** Drop in-memory OAuth popup / guard state so login buttons can open windows again. */
const clearWebOAuthRuntimeState = () => {
  if (Platform.OS !== 'web') return;
  try {
    getAuthSessionGuard().clearSession();
  } catch {
    /* best-effort */
  }
  try {
    const {
      dismissOAuthPopupFromOpener,
      clearOAuthSessionStorage,
    } = require('@utils/oauthWebPopupCompletion');
    dismissOAuthPopupFromOpener();
    clearOAuthSessionStorage();
  } catch {
    /* best-effort */
  }
};

const clearWebStorageForLogout = () => {
  if (Platform.OS !== 'web' || typeof document === 'undefined') return;
  clearWebOAuthRuntimeState();
  clearSessionCookie();
  clearPersistentSessionMeta();
  if (typeof localStorage !== 'undefined') {
    localStorage.clear();
  }
};

import {
  isDefiniteSessionExpiredAxiosError,
  type AxiosLikeError,
} from '@utils/sessionExpiryDetection';

let sessionExpiryInFlight = false;

export function maybeHandleSessionExpiredFromAxiosError(
  error: AxiosLikeError
): boolean {
  const isAuthenticated = !!store.getState().promiseData?.isAuthenticated;

  if (
    !isDefiniteSessionExpiredAxiosError(error, isAuthenticated) ||
    isWithinAuthGracePeriod()
  ) {
    return false;
  }

  void handleSessionExpired();
  return true;
}

export function navigateToLoginScreen(): void {
  if (!navigationRef.isReady()) return;

  abortAllRequests();

  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    const path = window.location.pathname;
    if (path.includes('/dashboard')) {
      const newUrl = new URL(window.location.href);
      newUrl.pathname = '/login';
      newUrl.search = '';
      window.history.replaceState({}, '', newUrl.toString());
    }
  }

  if (Platform.OS === 'web') {
    navigationRef.dispatch(
      CommonActions.reset({
        index: 0,
        routes: [{ name: 'Auth' }],
      })
    );
    return;
  }

  navigationRef.dispatch(
    CommonActions.reset({
      index: 0,
      routes: [{ name: 'Login' }],
    })
  );
}

export type HandleSessionExpiredOptions = {
  queryClient?: QueryClient;
  /** When true, skip the brief session-expired toast (e.g. visibility re-check). */
  silent?: boolean;
};

export type PerformUserLogoutOptions = {
  queryClient?: QueryClient;
  /** Brief loading route before login (default true for explicit logout). */
  useLoadingTransition?: boolean;
  loadingMessage?: string;
  successMessage?: string;
};

/**
 * Intentional user logout — clears auth, navigates to login (web: Auth stack), best-effort backend cleanup.
 */
export async function performUserLogout(
  options: PerformUserLogoutOptions = {}
): Promise<void> {
  const {
    queryClient,
    useLoadingTransition = true,
    loadingMessage = t('safelyLoggingOut'),
    successMessage = t('logoutSuccess'),
  } = options;

  try {
    clearAuthGracePeriod();
    store.dispatch({ type: HIDE_ALL_MODALS });
    queryClient?.removeQueries({ queryKey: ['subscriptionPlan'] });
    queryClient?.clear();
    void clearOfflineModeEntitlement();

    if (useLoadingTransition && navigationRef.isReady()) {
      store.dispatch({ type: SHOW_LOADING_MODAL });
      const loadingRoute = Platform.OS === 'web' ? 'Loading' : 'LoadingScreen';
      navigationRef.navigate(loadingRoute as never, {
        flagHold: true,
        loadingMessage,
      } as never);
    }

    SocketManager.destroyAll();

    if (Platform.OS === 'web') {
      clearWebStorageForLogout();
      clearPendingDashboardDeepLink();
    } else {
      try {
        const Keychain = await import('react-native-keychain');
        await Keychain.resetGenericPassword();
      } catch {
        /* best-effort */
      }
    }

    store.dispatch({ type: LOGGED_OUT });
    clearWatchSessionLater();
    navigateToLoginScreen();

    void (async () => {
      try {
        const allKeys = await withTimeout(
          AsyncStorage.getAllKeys(),
          1500,
          'AsyncStorage.getAllKeys'
        );
        const walletPassKeys = allKeys.filter((key) => key.startsWith('wallet_pass_'));
        if (walletPassKeys.length > 0) {
          await withTimeout(
            AsyncStorage.multiRemove(walletPassKeys),
            1500,
            'AsyncStorage.multiRemove(wallet_pass_)'
          );
        }
      } catch (storageError) {
        if (__DEV__) {
          console.warn('⚠️ Logout cleanup: wallet pass cache not fully cleared:', storageError);
        }
      }

      try {
        const { logoutUser } = await import('@api/api');
        await withTimeout(logoutUser(), 5000, 'apis.logoutUser');
      } catch (e) {
        if (__DEV__) {
          console.warn('⚠️ Logout cleanup: backend logout failed/timed out:', e);
        }
      }

      try {
        await withTimeout(persistor.purge(), 3000, 'persistor.purge');
      } catch (e) {
        if (__DEV__) {
          console.warn('⚠️ Logout cleanup: persistor purge failed/timed out:', e);
        }
      }
    })();

    if (successMessage) {
      store.dispatch({
        type: SHOW_AUTH_MODAL,
        data: { content: successMessage, type: 'success' },
      });
    }
  } catch (error) {
    if (__DEV__) console.error('[performUserLogout]', error);
    store.dispatch({
      type: SHOW_AUTH_MODAL,
      data: { content: t('logoutFailed'), type: 'error' },
    });
    navigateToLoginScreen();
  } finally {
    store.dispatch({ type: HIDE_LOADING_MODAL });
    store.dispatch({ type: HIDE_ALL_MODALS });
  }
}

/**
 * Clears local auth state and returns the user to the login flow (web: Auth stack).
 * Safe to call from axios interceptors; debounced to avoid duplicate logouts.
 */
export async function handleSessionExpired(
  options: HandleSessionExpiredOptions = {}
): Promise<void> {
  if (sessionExpiryInFlight) return;
  if (isWithinAuthGracePeriod()) return;
  if (!store.getState().promiseData?.isAuthenticated) return;

  sessionExpiryInFlight = true;
  clearAuthGracePeriod();

  try {
    const { queryClient, silent = false } = options;

    store.dispatch({ type: HIDE_ALL_MODALS });
    queryClient?.removeQueries({ queryKey: ['subscriptionPlan'] });
    queryClient?.clear();
    void clearOfflineModeEntitlement();

    SocketManager.destroyAll();
    clearWebOAuthRuntimeState();
    clearSessionCookie();
    clearPersistentSessionMeta();

    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const currentPath = `${window.location.pathname}${window.location.search}`;
      if (isDashboardPath(window.location.pathname)) {
        stashPendingDashboardDeepLink(currentPath);
      }
    }

    store.dispatch({ type: LOGGED_OUT });
    clearWatchSessionLater();
    navigateToLoginScreen();

    if (!silent) {
      store.dispatch({
        type: SHOW_AUTH_MODAL,
        data: {
          content: t('sessionExpiredMessage'),
          type: 'error',
        },
      });
    }

    void (async () => {
      try {
        const { logoutUser } = await import('@api/api');
        await logoutUser();
      } catch {
        /* best-effort */
      }
      try {
        await persistor.purge();
      } catch {
        /* best-effort */
      }
    })();
  } finally {
    setTimeout(() => {
      sessionExpiryInFlight = false;
    }, 3000);
  }
}
